/**
 * One shared tooltip element. All text goes in via textContent - series and
 * category names originate in Firestore documents and are untrusted.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

let el = null;

function ensure(parent) {
  if (!el) {
    el = document.createElement('div');
    el.className = 'tooltip';
    el.setAttribute('role', 'tooltip');
    el.dataset.show = 'false';
  }
  if (el.parentElement !== parent) parent.appendChild(el);
  return el;
}

/** Let an embedded chart clean up its tooltip when Vue removes it. */
export function removeTooltip(parent) {
  if (el && parent?.contains(el)) {
    el.remove();
    el = null;
  }
}

/**
 * @param {{title?:string, sub?:string, rows?:Array<{label:string,value:string,color?:string,shape?:'line'|'dot'}>}} content
 */
export function showTooltip(content, x, y, parent = document.body) {
  const node = ensure(parent);
  node.textContent = '';

  if (content.title) {
    const t = document.createElement('div');
    t.className = 'tt-title';
    t.textContent = content.title;
    node.appendChild(t);
  }
  if (content.sub) {
    const s = document.createElement('div');
    s.className = 'tt-sub';
    s.textContent = content.sub;
    node.appendChild(s);
  }
  for (const row of content.rows || []) {
    const r = document.createElement('div');
    r.className = 'tt-row';
    if (row.color) {
      const key = document.createElement('span');
      key.className = row.shape === 'dot' ? 'tt-key-dot' : 'tt-key';
      key.style.background = row.color;
      r.appendChild(key);
    }
    // Value leads, label follows.
    const v = document.createElement('span');
    v.className = 'tt-value';
    v.textContent = row.value;
    r.appendChild(v);
    if (row.label) {
      const n = document.createElement('span');
      n.className = 'tt-name';
      n.textContent = row.label;
      r.appendChild(n);
    }
    node.appendChild(r);
  }

  node.dataset.show = 'true';
  position(node, x, y);
}

function position(node, x, y) {
  const pad = 12;
  const rect = node.getBoundingClientRect();
  let left = x + pad;
  let top = y - rect.height - pad;
  if (left + rect.width > window.innerWidth - 8) left = x - rect.width - pad;
  if (left < 8) left = 8;
  if (top < 8) top = y + pad;
  node.style.left = `${left}px`;
  node.style.top = `${top}px`;
}

export function hideTooltip() {
  if (el) el.dataset.show = 'false';
}

/**
 * Wires hover + keyboard focus to the same tooltip content, and gives the mark
 * a hit area that is larger than its paint.
 */
export function attachTip(target, contentFn) {
  const show = evt => {
    const box = target.getBoundingClientRect();
    const x = evt.clientX ?? box.left + box.width / 2;
    const y = evt.clientY ?? box.top;
    showTooltip(contentFn(), x, y, target.closest('[data-dashboard-charts]') || document.body);
  };
  target.addEventListener('pointerenter', show);
  target.addEventListener('pointermove', show);
  target.addEventListener('pointerleave', hideTooltip);
  target.addEventListener('focus', show);
  target.addEventListener('blur', hideTooltip);
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '0');
}

export const svg = (name, attrs = {}) => {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    node.setAttribute(k, String(v));
  }
  return node;
};
